import { useState } from "react";
import {
  Dialog,
  Heading,
  Divider,
  Content,
  ButtonGroup,
  Button,
  Flex,
  TextField,
  RadioGroup,
  Radio,
  Form,
} from "@adobe/react-spectrum";
import PhoneInput from "react-phone-number-input";
import { ContactsIcon } from "../../common/Icons";
import "./style.css";
import { FilterType, SpeedDialData } from "../../../types/generic.types";
import { showErrorToast } from "../../../utils/utils";
import {
  addSpeedDialContact,
  updateSpeedDialContact,
  getSpeedDialData,
} from "../../../api/session-api";
import { toast } from "react-toastify";

import storeRTK from "../../../store/storeRTK";
import {
  addSpeedDialToHistoryAction,
  editSpeedDialToHistoryAction,
} from "../../../connector/history";

// Generate a simple UUID
const generateId = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

interface AddToSpeedDialDialogProps {
  onClose: () => void;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  contactType?: FilterType;
  isEdit: boolean;
}

export function AddToSpeedDialDialog({
  onClose,
  phoneNumber,
  firstName,
  lastName,
  contactType,
  isEdit,
}: AddToSpeedDialDialogProps) {
  const [form, setForm] = useState<SpeedDialData>({
    phoneNumber: phoneNumber || "",
    contactType:
      contactType && contactType !== "all" ? contactType : "Customer",
    firstName: firstName || "",
    lastName: lastName || "",
  });

  const [loading, setLoading] = useState(false);
  const [existingContacts, setExistingContacts] = useState<any[]>([]);

  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof SpeedDialData) => (value: string) => {
    const sanitizeValue = (field: keyof SpeedDialData, value: string) => {
      if (field === "firstName" || field === "lastName") {
        return value.replace(/[^a-zA-Z0-9\s]/g, "").slice(0, 20);
      }
      return value;
    };

    setForm((prev) => ({ ...prev, [field]: sanitizeValue(field, value) }));
  };

  const handlePhoneNumberChange = (value: string | undefined) => {
    // Clean the number by removing all non-digit characters except the leading +
    const cleanedNumber = value
      ? value.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
      : "";

    // Ensure the number starts with + if it has digits
    const formattedNumber =
      cleanedNumber && !cleanedNumber.startsWith("+")
        ? `+${cleanedNumber}`
        : cleanedNumber;

    setForm((prev) => ({ ...prev, phoneNumber: formattedNumber }));
  };

  const validateForm = async () => {
    try {
      // Fetch current contacts to check for duplicates
      const response = await getSpeedDialData();
      if (response.status === "success" && response.contacts) {
        setExistingContacts(response.contacts);

        const phoneNumberExists = response.contacts.some(
          (contact: any) => contact.phoneNumber === form.phoneNumber
        );

        const maxSpeedDials = Number(process.env.MAX_SPEEDDIALS_ITEMS) || 100;

        const errorMessages = {
          phoneNumberExists: {
            message: "This phone number is already in your speed dials.",
            code: "phoneNumberExistsError",
          },
          speedDialsLimit: {
            message: `Maximum of ${maxSpeedDials} speed dials reached. Please remove an existing speed dial before adding a new one.`,
            code: "speedDialsLimitError",
          },
        };

        if (!isEdit && phoneNumberExists) {
          showErrorToast(
            errorMessages.phoneNumberExists.message,
            errorMessages.phoneNumberExists.code
          );
          return false;
        }

        if (!isEdit && response.contacts.length >= maxSpeedDials) {
          showErrorToast(
            errorMessages.speedDialsLimit.message,
            errorMessages.speedDialsLimit.code
          );
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error("Error validating form:", error);
      showErrorToast(
        "Failed to validate contact. Please try again.",
        "validationError"
      );
      return false;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Validate form first
      const isValid = await validateForm();
      if (!isValid) {
        setLoading(false);
        return;
      }

      if (isEdit) {
        // For editing, try to find the existing contact ID
        const existingContact = existingContacts.find(
          (contact: any) => contact.phoneNumber === phoneNumber
        );
        const contactId = existingContact?.id || generateId();

        const result = await updateSpeedDialContact(
          form.firstName,
          form.lastName,
          form.phoneNumber,
          contactId,
          form.contactType
        );

        if (result) {
          toast.success("Contact updated successfully");
          storeRTK.dispatch(
            editSpeedDialToHistoryAction({
              originalPhoneNumber: phoneNumber || "",
              updatedContact: form,
            })
          );
          onClose();
        } else {
          showErrorToast(
            "Failed to update contact. Please try again.",
            "updateError"
          );
        }
      } else {
        // For new contacts, generate a new ID
        const newId = generateId();

        const result = await addSpeedDialContact(
          form.firstName,
          form.lastName,
          form.phoneNumber,
          newId,
          form.contactType
        );

        if (result) {
          storeRTK.dispatch(addSpeedDialToHistoryAction(form));
          toast.success("Contact added to speed dials");
          onClose();
        } else {
          showErrorToast(
            "Failed to add contact. Please try again.",
            "addError"
          );
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showErrorToast("An error occurred. Please try again.", "submitError");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <ContactsIcon color="#222222" height="24.89px" width="19.65px" />
          <span>
            {isEdit
              ? `Update ${form.phoneNumber || "contact"} in speed dials`
              : `Add ${form.phoneNumber || "contact"} to speed dials`}
          </span>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form width="100%">
          <Flex direction="row" gap="size-200" alignItems="start">
            {/* Phone Input + Error message */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  marginBottom: "0.25rem",
                  color: "#222222",
                }}
              >
                Phone number
              </label>
              <PhoneInput
                value={form.phoneNumber}
                onChange={(value) => {
                  if (value && value.length <= 15) {
                    handlePhoneNumberChange(value);
                    setError(null);
                  } else {
                    setError("Phone number must be less than 15 digits.");
                  }
                }}
                international
                className="speed-dial-phone-input"
                defaultCountry="US"
                disabled={isEdit}
                onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                  const pastedData = event.clipboardData.getData("text");
                  // Clean the pasted data by removing all non-digit characters except +
                  const cleanedPastedData = pastedData
                    .replace(/[^\d+]/g, "")
                    .replace(/\+(\d)/g, "+$1");
                  const formattedPastedData =
                    cleanedPastedData && !cleanedPastedData.startsWith("+")
                      ? `+${cleanedPastedData}`
                      : cleanedPastedData;
                  setForm((prev) => ({
                    ...prev,
                    phoneNumber: formattedPastedData,
                  }));
                  event.preventDefault();
                }}
                style={
                  isEdit
                    ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" }
                    : { backgroundColor: "#ffffff" }
                }
              />
              {error && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            <RadioGroup
              label="Save contact as"
              value={form.contactType}
              onChange={handleChange("contactType")}
              orientation="horizontal"
              flex={1}
              isDisabled={loading}
              UNSAFE_className="speed-dial-radio-group"
            >
              <Radio value="Customer" UNSAFE_className="speed-dial-radio-btn">
                Customer
              </Radio>
              <Radio value="Internal" UNSAFE_className="speed-dial-radio-btn">
                Internal
              </Radio>
            </RadioGroup>
          </Flex>
          <Flex direction="row" gap="size-200">
            <TextField
              label="First name"
              value={form.firstName}
              onChange={handleChange("firstName")}
              maxLength={20}
              description="20 characters limit"
              isDisabled={loading}
              flex={1}
            />
            <TextField
              label="Last name"
              value={form.lastName}
              onChange={handleChange("lastName")}
              maxLength={20}
              description="20 characters limit"
              isDisabled={loading}
              flex={1}
            />
          </Flex>
        </Form>
      </Content>

      {/* Buttons */}
      <ButtonGroup align="end" marginTop="size-200">
        <Button variant="secondary" onPress={onClose} isDisabled={loading}>
          Cancel
        </Button>
        <Button
          variant="cta"
          onPress={() => {
            handleSubmit();
          }}
          isDisabled={
            !form.phoneNumber ||
            !form.firstName ||
            !form.lastName ||
            !!error ||
            loading
          }
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Adding..."
            : isEdit
            ? "Update speed dial"
            : "Add to speed dial"}
        </Button>
      </ButtonGroup>
    </Dialog>
  );
}
